import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Newspaper, Loader2, ArrowRight, Flame } from "lucide-react";
import { API, apiFetch } from "@/lib/gp";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const fmt = (iso) => new Date(iso).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });

const GTA_LINKS = [
  ["GTA 6 release date", "/gta-6/release-date"],
  ["Pre-order & bonuses", "/gta-6/pre-order"],
  ["Standard vs Ultimate", "/gta-6/editions"],
  ["Jason & Lucia", "/gta-6/jason-and-lucia"],
  ["Vice City & Leonida map", "/gta-6/map-and-locations"],
  ["PC release status", "/gta-6/pc"],
  ["60 FPS status", "/gta-6/60-fps"],
  ["GTA 6 Online status", "/gta-6/online"],
];

export default function News() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", body: "" });
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(() => {
    fetch(`${API}/news`).then((r) => r.json()).then((d) => setItems(d.news || [])).catch(() => {}).finally(() => setLoaded(true));
  }, []);
  useEffect(() => { load(); }, [load]);

  const post = async (e) => {
    e.preventDefault();
    if (!form.title || !form.body) return;
    setBusy(true);
    try {
      const res = await apiFetch("/news", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) });
      if (!res.ok) throw new Error();
      toast.success("Post published.");
      setForm({ title: "", body: "" });
      load();
    } catch { toast.error("Could not publish (admins only)."); } finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-5xl px-5 md:px-8 py-12 md:py-16">
      <div className="flex items-center gap-3 mb-2">
        <Newspaper className="text-gp-blue" size={26} />
        <h1 className="font-unbounded font-black tracking-tighter text-4xl uppercase">News</h1>
      </div>
      <p className="text-white/60 mb-8">Gaming news, launch watch, strategy and GamePlatPlay community updates.</p>

      <section className="glass rounded-3xl p-7 md:p-9 mb-8 ring-1 ring-gp-gold/30" data-testid="gta6-news-feature">
        <div className="flex items-center gap-2 text-gp-gold text-sm uppercase tracking-widest font-semibold">
          <Flame size={17} /> Launch Watch
        </div>
        <h2 className="font-unbounded font-black tracking-tighter text-3xl md:text-4xl mt-3">GTA 6 Command Center</h2>
        <p className="text-white/65 mt-3 max-w-3xl leading-relaxed">
          GTA VI is scheduled for November 19, 2026. GamePlatPlay now tracks the biggest search questions in one confirmed-facts hub — release timing, pre-orders, editions, Jason and Lucia, Vice City, platforms, performance questions and more.
        </p>
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {GTA_LINKS.map(([label, to]) => (
            <Link key={to} to={to} className="rounded-xl border border-white/10 bg-black/25 px-4 py-3 text-sm font-semibold text-white/80 hover:text-white hover:border-gp-blue/40 transition-colors">
              {label}
            </Link>
          ))}
        </div>
        <Link to="/gta-6" className="mt-6 inline-flex items-center gap-2 text-gp-blue font-bold hover:text-white transition-colors">
          Explore all GTA 6 coverage <ArrowRight size={16} />
        </Link>
      </section>

      {user && user.is_admin && (
        <form onSubmit={post} className="glass rounded-2xl p-6 mb-8 space-y-3" data-testid="news-form">
          <p className="font-semibold text-gp-gold text-sm uppercase tracking-widest">Admin: publish a post</p>
          <Input data-testid="news-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Headline" className="bg-black/30 border-white/10 h-11 rounded-xl" />
          <Textarea data-testid="news-body" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Write your update…" rows={4} className="bg-black/30 border-white/10 rounded-xl resize-none" />
          <Button type="submit" data-testid="news-publish" disabled={busy} className="rounded-full bg-gp-blue font-bold px-6">{busy ? <Loader2 className="animate-spin" size={16} /> : "Publish"}</Button>
        </form>
      )}

      <div className="space-y-5" data-testid="news-list">
        {!loaded && (
          <div className="grid place-items-center py-16 text-white/45"><Loader2 className="animate-spin" size={24} /></div>
        )}
        {loaded && items.length === 0 && (
          <section className="glass rounded-2xl p-7 text-center">
            <h2 className="font-unbounded font-bold text-2xl">More posts are coming</h2>
            <p className="mx-auto mt-3 max-w-xl text-white/60">
              The GTA 6 command center is live now. Yorick, tournament updates and fresh gaming posts will continue landing here.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link to="/gta-6" className="rounded-full bg-gp-blue px-6 py-3 font-bold text-white hover:bg-gp-blue/90">Explore GTA 6</Link>
              <Link to="/balatro" className="rounded-full border border-white/20 px-6 py-3 font-semibold text-white/80 hover:bg-white/5">Explore Balatro</Link>
            </div>
          </section>
        )}
        {items.map((n) => (
          <article key={n.id} className="glass rounded-2xl p-6">
            <p className="text-xs text-white/40 uppercase tracking-widest">{fmt(n.created_at)}</p>
            <h2 className="font-unbounded font-bold text-xl mt-1">{n.title}</h2>
            <p className="text-white/75 mt-2 leading-relaxed whitespace-pre-line">{n.body}</p>
          </article>
        ))}
      </div>
    </div>
  );
}
